// Core packages
import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";

// Third party packages
import {
  DateAdapter, MatNativeDateModule, MatPaginatorIntl, MAT_DATE_FORMATS, MAT_DATE_LOCALE
} from "@angular/material";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter
} from "@angular/material-moment-adapter";
import { NgSelectModule } from "@ng-select/ng-select";
import { FormlyModule } from "@ngx-formly/core";
import { FormlyMaterialModule } from "@ngx-formly/material";
import { FormlyMatDatepickerModule } from "@ngx-formly/material/datepicker";
// FormlyMaterialModule only registers form-field/input/textarea/radio/checkbox/
// multicheckbox/select: 'toggle' ships as a separate opt-in module, exactly like the
// datepicker one. Without it a `type: 'toggle'` field throws while the form is being
// built, which aborts FormlyForm.ngOnChanges BEFORE it subscribes to the value
// changes: the field does not render AND the whole model stops syncing.
import { FormlyMatToggleModule } from "@ngx-formly/material/toggle";
import {
  TranslateModule, TranslateService
} from "@ngx-translate/core";
import { QRCodeModule } from "angularx-qrcode";
import { MatProgressButtonsModule } from "mat-progress-buttons";
import { NgxFileDropModule } from "ngx-file-drop";
import { GooglePlaceModule } from "ngx-google-places-autocomplete";
import { ToastrModule } from "ngx-toastr";
import { MaterialModule } from "../material.module";
import { PaginatorIntlService } from "../shared/utils/it-paginator-intl";
// import { NgxBarcode6Module } from 'ngx-barcode6';

// Custom packages
import { AuthGuard } from "../shared/guards/auth.guard";
import { LangGuard } from "../shared/guards/lang.guard";
import { ApiService } from "../shared/services/api.service";
import { ConfigService } from "../shared/services/config.service";
import { EnvironmentService } from "../shared/services/environment.service";
import { ListsService } from "../shared/services/lists.service";
import { SidenavService } from "../shared/services/sidenav.service";
import { ValidationLoaderService } from "../shared/services/validations-loader.service";
import { AuthService } from "./auth/auth.service";
// import { AutocompleteTypeComponent } from '../shared/components/formly/autocomplete-type/autocomplete-type.component';
// import { GoogleAutocompleteTypeComponent } from '../shared/components/formly/google-autocomplete-type/google-autocomplete-type.component';
import { CardSpinnerComponent } from "../shared/components/card-spinner/card-spinner.component";
import { CreatorUpdatorBoxComponent } from "../shared/components/creator-updator-box/creator-updator-box.component";
import { FileUploaderComponent } from "../shared/components/file-uploader/file-uploader.component";
import { PageHeaderComponent } from "../shared/components/page-header/page-header.component";
import {
  DateDocumentFormatPipe, DateFormatPipe,
  DateTimeFormatPipe, MinutesToHoursPipe, TimeFormatPipe,
} from "../shared/pipes/date-format.pipe";
import { ObjToKeysPipe } from "../shared/pipes/object-to-keys.pipe";

import { AddPlatformNoteDialogComponent } from 'src/app/components/platform-note-card/components/add-platform-note-dialog/add-platform-note-dialog.component';
import { AssociateBalancesPlatformNoteDialogComponent } from "src/app/components/platform-note-card/components/associate-balances-platform-note-dialog/associate-balances-platform-note-dialog.component";
import { AssociatePropertiesPlatformNoteDialogComponent } from 'src/app/components/platform-note-card/components/associate-properties-platform-note-dialog/associate-properties-platform-note-dialog.component';
import { EditPlatformNoteDialogComponent } from 'src/app/components/platform-note-card/components/edit-platform-note-dialog/edit-platform-note-dialog.component';
import { ViewPlatformNoteDialogComponent } from 'src/app/components/platform-note-card/components/view-platform-note-dialog/view-platform-note-dialog.component';
import { ViewPlatformNotesDialogComponent } from "src/app/components/platform-note-card/components/view-platform-notes-dialog/view-platform-notes-dialog.component";
import { PlatformNoteCardComponent } from 'src/app/components/platform-note-card/platform-note-card.component';

import { CreditsafeService } from "../shared/services/creditsafe.service";
import { GoogleapiService } from "../shared/services/google.service";
import { OpenapiService } from "../shared/services/openapi.service";

export const CUSTOM_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY"
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MM YYYY",
    dateA11yLabel: "DD/MM/YYYY",
    monthYearA11yLabel: "MM YYYY"
  }
};

export function MatPaginatorIntlCustomFactory(
  translate: TranslateService
): any {
  const service = new PaginatorIntlService();
  service.injectTranslateService(translate);
  return service;
}

@NgModule({
  imports: [
    // Core modules
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatNativeDateModule,

    // Third party modules
    MaterialModule,
    TranslateModule.forChild(),
    ToastrModule.forRoot(),
    FormlyModule.forChild(),
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    FormlyMatToggleModule,
    MatProgressButtonsModule.forRoot(),
    NgSelectModule,
    NgxFileDropModule,
    GooglePlaceModule,
    // ChartsModule,
    // NgSelectModule,
    QRCodeModule,
    // NgxBarcode6Module,

    // Custom
  ],
  declarations: [
    // Utils
    TimeFormatPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    DateDocumentFormatPipe,
    MinutesToHoursPipe,
    ObjToKeysPipe,

    // Components
    PageHeaderComponent,
    CreatorUpdatorBoxComponent,
    CardSpinnerComponent,
    FileUploaderComponent,

    PlatformNoteCardComponent,
    AddPlatformNoteDialogComponent,
    ViewPlatformNoteDialogComponent,
    EditPlatformNoteDialogComponent,
    AssociatePropertiesPlatformNoteDialogComponent,
    ViewPlatformNotesDialogComponent,
    AssociateBalancesPlatformNoteDialogComponent,
  ],
  exports: [
    // Core modules
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,

    // Third party modules
    MaterialModule,
    TranslateModule,
    ToastrModule,
    FormlyModule,
    FormlyMaterialModule,
    // FormlyMatDatepickerModule,
    NgSelectModule,
    MatProgressButtonsModule,
    NgxFileDropModule,
    GooglePlaceModule,
    // ChartsModule,
    // NgSelectModule,
    QRCodeModule,
    // NgxBarcode6Module,

    // Utils
    TimeFormatPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    DateDocumentFormatPipe,
    MinutesToHoursPipe,
    ObjToKeysPipe,

    // Components
    PageHeaderComponent,
    CreatorUpdatorBoxComponent,
    CardSpinnerComponent,
    FileUploaderComponent,
    
    PlatformNoteCardComponent,
    AddPlatformNoteDialogComponent,
    ViewPlatformNoteDialogComponent,
    EditPlatformNoteDialogComponent,
    AssociatePropertiesPlatformNoteDialogComponent,
    ViewPlatformNotesDialogComponent,
    AssociateBalancesPlatformNoteDialogComponent,
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        // Misc
        {
          provide: MatPaginatorIntl,
          useFactory: MatPaginatorIntlCustomFactory,
          deps: [TranslateService]
          // useValue: getItalianPaginatorIntl()
        },
        {
          provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
          useValue: { useUtc: true }
        },
        {
          provide: DateAdapter,
          useClass: MomentDateAdapter,
          deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_FORMATS },

        // Services
        SidenavService,
        AuthService,
        ApiService,
        ConfigService,
        EnvironmentService,
        ListsService,
        AuthGuard,
        LangGuard,
        ValidationLoaderService,
        OpenapiService,
        CreditsafeService,
        GoogleapiService,
      ]
    };
  }
}
